import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { PageHeader } from './ui';
import './LenderManagement.css';

interface Lender {
  name: string;
  email: string;
  hasEmail: boolean;
  aliasCount?: number;
  locDisabled?: boolean;
  lenderDisabled?: boolean;
}

interface LenderAlias {
  alias: string;
  canonical: string;
}

interface LenderManagementData {
  lenders: Lender[];
  totalCount: number;
  filteredCount: number;
  search: string;
}

interface LenderManagementProps {
  refreshKey: number;
}

export const LenderManagement: React.FC<LenderManagementProps> = ({ refreshKey }) => {
  const { apiRequest } = useAuth();
  const [lenders, setLenders] = useState<Lender[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [filteredCount, setFilteredCount] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  
  // Add/Edit form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingLender, setEditingLender] = useState<Lender | null>(null);
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formLocDisabled, setFormLocDisabled] = useState(false);
  const [formLenderDisabled, setFormLenderDisabled] = useState(false);
  const [formErrors, setFormErrors] = useState<{ name?: string; email?: string }>({});
  
  // Import/Export
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Alias viewing
  const [showAliases, setShowAliases] = useState<string | null>(null);
  const [aliases, setAliases] = useState<LenderAlias[]>([]);
  const [loadingAliases, setLoadingAliases] = useState(false);

  const loadLenders = async (search?: string) => {
    setLoading(true);
    setError('');
    try {
      const params = search !== undefined ? `?search=${encodeURIComponent(search)}` : '';
      const response = await apiRequest({
        method: 'GET',
        url: `/ui/admin/lenders${params}`
      }) as LenderManagementData;
      
      setLenders(response.lenders);
      setTotalCount(response.totalCount);
      setFilteredCount(response.filteredCount);
    } catch (err: any) {
      console.error('Error loading lenders:', err);
      setError(err.response?.data?.error || 'Failed to load lenders');
    } finally {
      setLoading(false);
    }
  };

  const refreshFromSource = async () => {
    setError('');
    try {
      setRefreshing(true);
      await apiRequest({
        method: 'POST',
        url: '/ui/admin/lenders/reload'
      });
    } catch (err: any) {
      console.error('Error refreshing lenders from Cosmos:', err);
      setError(err.response?.data?.error || err.message || 'Failed to refresh lenders');
    } finally {
      setRefreshing(false);
    }

    await loadLenders(searchQuery);
  };

  useEffect(() => {
    if (refreshKey > 0) {
      refreshFromSource();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    loadLenders(query);
  };

  const loadAliases = async (lenderName: string) => {
    if (showAliases === lenderName) {
      // Toggle off
      setShowAliases(null);
      return;
    }

    setLoadingAliases(true);
    setShowAliases(lenderName);
    try {
      const response = await apiRequest({
        method: 'GET',
        url: `/ui/admin/lenders/${encodeURIComponent(lenderName)}/aliases`
      }) as { lender: string; aliases: LenderAlias[]; count: number };
      
      setAliases(response.aliases);
    } catch (err: any) {
      console.error('Error loading aliases:', err);
      setError(err.response?.data?.error || 'Failed to load aliases');
      setShowAliases(null);
    } finally {
      setLoadingAliases(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: { name?: string; email?: string } = {};
    
    if (!formName.trim()) {
      errors.name = 'Lender name is required';
    }
    
    if (!formEmail.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formEmail)) {
      errors.email = 'Invalid email format';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setError('');
    setSuccessMessage('');
    
    try {
      if (editingLender) {
        // Update existing lender
        await apiRequest({
          method: 'PUT',
          url: `/ui/admin/lenders/${encodeURIComponent(editingLender.name)}`,
          data: {
            email: formEmail,
            name: formName !== editingLender.name ? formName : undefined,
            locDisabled: formLocDisabled,
            lenderDisabled: formLenderDisabled
          }
        });
        setSuccessMessage(`Successfully updated lender '${formName}'`);
      } else {
        // Add new lender
        await apiRequest({
          method: 'POST',
          url: '/ui/admin/lenders',
          data: {
            name: formName,
            email: formEmail
          }
        });
        setSuccessMessage(`Successfully added lender '${formName}'`);
      }
      
      // Reset form and reload
      setShowAddForm(false);
      setEditingLender(null);
      setFormName('');
      setFormEmail('');
      setFormLocDisabled(false);
      setFormLenderDisabled(false);
      setFormErrors({});
      loadLenders(searchQuery);
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
      
    } catch (err: any) {
      console.error('Error saving lender:', err);
      setError(err.response?.data?.error || 'Failed to save lender');
    }
  };

  const handleEdit = (lender: Lender) => {
    setEditingLender(lender);
    setFormName(lender.name);
    setFormEmail(lender.email);
    setFormLocDisabled(lender.locDisabled || false);
    setFormLenderDisabled(lender.lenderDisabled || false);
    setFormErrors({});
    setShowAddForm(true);
  };

  const handleDelete = async (lender: Lender) => {
    if (!window.confirm(`Are you sure you want to delete '${lender.name}'?`)) {
      return;
    }
    
    setError('');
    setSuccessMessage('');
    
    try {
      await apiRequest({
        method: 'DELETE',
        url: `/ui/admin/lenders/${encodeURIComponent(lender.name)}`
      });
      
      setSuccessMessage(`Successfully deleted lender '${lender.name}'`);
      loadLenders(searchQuery);
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
      
    } catch (err: any) {
      console.error('Error deleting lender:', err);
      setError(err.response?.data?.error || 'Failed to delete lender');
    }
  };

  const handleDeleteAlias = async (lenderName: string, aliasName: string) => {
    if (!window.confirm(`Are you sure you want to remove the alias "${aliasName}"?`)) {
      return;
    }
    
    setError('');
    setSuccessMessage('');
    
    try {
      await apiRequest({
        method: 'DELETE',
        url: `/ui/admin/lenders/${encodeURIComponent(lenderName)}/aliases/${encodeURIComponent(aliasName)}`
      });
      
      setSuccessMessage(`Successfully removed alias '${aliasName}'`);
      
      // Reload aliases for this lender
      await loadAliases(lenderName);
      
      // Reload lenders to update alias count
      await loadLenders(searchQuery);
      
      setTimeout(() => setSuccessMessage(''), 5000);
      
    } catch (err: any) {
      console.error('Error deleting alias:', err);
      setError(err.response?.data?.error || 'Failed to delete alias');
    }
  };

  const handleExport = async () => {
    try {
      // Get the auth token for manual fetch
      const token = localStorage.getItem('token');
      const headers: any = {
        'Accept': 'text/csv',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Use fetch directly for blob response
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/ui/admin/lenders/export`, {
        method: 'GET',
        headers: headers
      });
      
      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `lenders_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSuccessMessage('Successfully exported lenders');
      setTimeout(() => setSuccessMessage(''), 5000);
      
    } catch (err: any) {
      console.error('Error exporting lenders:', err);
      setError(err.message || 'Failed to export lenders');
    }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    setError('');
    setSuccessMessage('');
    
    try {
      // Get the auth token for manual fetch
      const token = localStorage.getItem('token');
      const headers: any = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Use fetch directly for FormData
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/ui/admin/lenders/import`, {
        method: 'POST',
        headers: headers,
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Import failed: ${response.statusText}`);
      }
      
      const data = await response.json();
      const result = data.result;
      
      setSuccessMessage(
        `Import complete: ${result.added} added, ${result.updated} updated, ${result.skipped} skipped`
      );
      
      loadLenders(searchQuery);
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
      
    } catch (err: any) {
      console.error('Error importing lenders:', err);
      setError(err.message || 'Failed to import lenders');
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const cancelForm = () => {
    setShowAddForm(false);
    setEditingLender(null);
    setFormName('');
    setFormEmail('');
    setFormLocDisabled(false);
    setFormLenderDisabled(false);
    setFormErrors({});
  };

  return (
    <div className="lender-management">
      <div className="lender-management-header">
        <PageHeader title="Lender Management" />
        <div className="header-actions">
          <button
            onClick={refreshFromSource}
            className="refresh-button"
            disabled={loading || refreshing}
            title="Reload lenders from Cosmos"
          >
            {refreshing ? 'Refreshing…' : '↻ Refresh'}
          </button>
          <button onClick={() => setShowAddForm(true)} className="add-button">
            + Add Lender
          </button>
          <button onClick={handleExport} className="export-button">
            📥 Export CSV
          </button>
          <button 
            onClick={() => fileInputRef.current?.click()} 
            className="import-button"
          >
            📤 Import CSV
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleImport}
            style={{ display: 'none' }}
          />
        </div>
      </div>

      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="lender-management-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search lenders by name or email..."
            value={searchQuery}
            onChange={handleSearch}
            className="search-input"
          />
          {searchQuery && (
            <span className="search-results">
              Showing {filteredCount} of {totalCount} lenders
            </span>
          )}
        </div>
      </div>

      {showAddForm && (
        <div className="lender-form-container">
          <form onSubmit={handleSubmit} className="lender-form">
            <h4>{editingLender ? 'Edit Lender' : 'Add New Lender'}</h4>
            
            <div className="form-group">
              <label htmlFor="lenderName">Lender Name *</label>
              <input
                id="lenderName"
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                className={formErrors.name ? 'error' : ''}
                placeholder="e.g., BMW Finance"
              />
              {formErrors.name && (
                <span className="field-error">{formErrors.name}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="lenderEmail">Email Address *</label>
              <input
                id="lenderEmail"
                type="email"
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                className={formErrors.email ? 'error' : ''}
                placeholder="e.g., complaints@bmwfinance.com"
              />
              {formErrors.email && (
                <span className="field-error">{formErrors.email}</span>
              )}
            </div>

            {editingLender && (
              <>
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formLocDisabled}
                      onChange={(e) => setFormLocDisabled(e.target.checked)}
                    />
                    <span>Disable LOC Sending (Bulk Submission Lender)</span>
                  </label>
                  <p className="field-hint">
                    When enabled, claims for this lender will not receive automated LOCs - they are handled via bulk submission instead.
                  </p>
                </div>
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formLenderDisabled}
                      onChange={(e) => setFormLenderDisabled(e.target.checked)}
                    />
                    <span>Fully Disable Lender</span>
                  </label>
                  <p className="field-hint">
                    When enabled, no LOCs will be sent to this lender at all — manual or automated. Use this for lenders that should not receive any correspondence.
                  </p>
                </div>
              </>
            )}

            <div className="form-actions">
              <button type="submit" className="save-button">
                {editingLender ? 'Update' : 'Add'} Lender
              </button>
              <button type="button" onClick={cancelForm} className="cancel-button">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading lenders...</div>
      ) : (
        <div className="lender-table-container">
          <table className="lender-table">
            <thead>
              <tr>
                <th>Lender Name</th>
                <th>Email Address</th>
                <th>Aliases</th>
                <th>LOC</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {lenders.length === 0 ? (
                <tr>
                  <td colSpan={6} className="no-data">
                    {searchQuery ? 'No lenders found matching your search' : 'No lenders found'}
                  </td>
                </tr>
              ) : (
                lenders.map((lender) => (
                  <React.Fragment key={lender.name}>
                    <tr>
                      <td className="lender-name">{lender.name}</td>
                      <td className="lender-email">
                        {lender.email || <span className="no-email">No email</span>}
                      </td>
                      <td className="aliases-column">
                        {lender.aliasCount ? (
                          <button
                            className="alias-count-button"
                            onClick={() => loadAliases(lender.name)}
                            title="Click to view aliases"
                          >
                            {lender.aliasCount} alias{lender.aliasCount !== 1 ? 'es' : ''}
                          </button>
                        ) : (
                          <span className="no-aliases">None</span>
                        )}
                      </td>
                      <td>
                        {lender.lenderDisabled ? (
                          <span className="status-badge" style={{ backgroundColor: '#dc2626', color: 'white' }} title="Lender fully disabled - no LOCs sent">Disabled</span>
                        ) : lender.locDisabled ? (
                          <span className="status-badge bulk-submission" title="LOC sending disabled - bulk submission lender">Bulk</span>
                        ) : (
                          <span className="status-badge loc-enabled" title="LOC sending enabled">Enabled</span>
                        )}
                      </td>
                      <td>
                        {lender.hasEmail ? (
                          <span className="status-badge active">Active</span>
                        ) : (
                          <span className="status-badge inactive">No Email</span>
                        )}
                      </td>
                      <td className="actions">
                        <button
                          onClick={() => handleEdit(lender)}
                          className="edit-button"
                          title="Edit lender"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(lender)}
                          className="delete-button"
                          title="Delete lender"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                    {showAliases === lender.name && (
                      <tr className="alias-details-row">
                        <td colSpan={6}>
                          <div className="alias-details">
                            <h4>Aliases for {lender.name}:</h4>
                            {loadingAliases ? (
                              <div className="loading">Loading aliases...</div>
                            ) : aliases.length === 0 ? (
                              <p>No aliases found</p>
                            ) : (
                              <ul className="alias-list">
                                {aliases.map((alias, index) => (
                                  <li key={index}>
                                    <span>{alias.alias}</span>
                                    <button
                                      className="delete-alias-button"
                                      onClick={() => handleDeleteAlias(lender.name, alias.alias)}
                                      title="Remove this alias"
                                    >
                                      ✕
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
          
          {lenders.length > 0 && (
            <div className="table-footer">
              Total: {filteredCount} lender{filteredCount !== 1 ? 's' : ''}
              {lenders.filter(l => !l.hasEmail).length > 0 && ` (${lenders.filter(l => !l.hasEmail).length} without email)`}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LenderManagement;
